import { useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import { ROUTES } from "@/app/routes";

export type LoginStage = "enter-email" | "check-email";

/**
 * Where the magic-link flow is. On /login the stage lives in the `stage`
 * search param so refresh and Back keep the OTP screen. With a `returnTo` the
 * form is rendered inside another page's URL (the AI app consent screen), so
 * the stage stays in component state: navigating to /login would drop the
 * consent request the person is signing in for.
 */
export function useLoginStage(returnTo: string | undefined) {
  const navigate = useNavigate();
  // reason: strict:false because AppShell's anonymous-user gate renders the
  // login INSIDE any app-layout route's URL (e.g. /workspaces/foo), not just
  // under /login. A typed getRouteApi(ROUTES.auth.login.id).useSearch() would
  // invariant-throw at those URLs, a white screen.
  const search = useSearch({ strict: false }) as { stage?: LoginStage };
  const [localStage, setLocalStage] = useState<LoginStage>("enter-email");
  const stage = returnTo ? localStage : (search.stage ?? "enter-email");

  const setStage = useCallback(
    async (next: LoginStage): Promise<void> => {
      if (returnTo) {
        setLocalStage(next);
        return;
      }
      await navigate({
        to: ROUTES.auth.login.path,
        search: next === "enter-email" ? {} : { stage: next },
        replace: true,
      });
    },
    [navigate, returnTo],
  );

  return { stage, setStage };
}
